/**
 * Der Abonnieren-Link im Infodienst-Kopfbereich.
 * Prototype: Iww.NodeTypes:Components.Molecules.Kopfbereich.Informationsdienst
 *
 * Ziel des Buttons ist die Shop-Seite des Produkts, also das Feld "url" der
 * Product-Delivery. Sie wird wie beim Leseproben-Stoerer erst im Browser geholt,
 * damit die Seitenauslieferung nicht auf den ProductRecommender wartet.
 *
 * Erst mit bekanntem Ziel wird der Link sichtbar; bleibt das Ziel aus,
 * verschwindet er ganz, statt ins Leere zu fuehren.
 *
 * Ob jemand Abonnent ist, entscheidet weiterhin der Server: der Kopfbereich
 * liegt im selben uservisibility-Wrapper wie der Stoerer, der den Button bei
 * vorhandener Infodienst-Rolle gar nicht erst ausliefert.
 */

const iwwAboButtonSelector = 'a[data-abo-button]';
const iwwAboButtonInitialized = new WeakSet();
const iwwAboButtonCacheLifetime = 60 * 60 * 1000;
// Gleicher Schluessel und gleiches Limit wie in freeTrialBannerPreview.js:
// Stoerer und Button teilen sich dieselbe Antwort und damit einen Abruf je Infodienst.
const iwwAboButtonCachePrefix = 'iww:free-trial-products:v2:';
const iwwAboButtonProductLimit = '1';
const iwwAboButtonProductCache = new Map();
const iwwAboButtonProductRequests = new Map();

const iwwAboButtonBaseUrl = (function () {
	if (window.location.hostname.endsWith('.internal')) {
		return 'https://productrecommender.iww.internal';
	}
	try {
		return process.env.PRODUCTRECOMMENDER_URL || 'https://productrecommender.iww.de';
	} catch {
		return 'https://productrecommender.iww.de';
	}
})();

function iwwAboButtonIsWebUrl(value) {
	if (!value || /[\s\\]/.test(value)) return false;
	if (value.startsWith('/') && !value.startsWith('//')) return true;
	try {
		return ['http:', 'https:'].includes(new URL(value).protocol);
	} catch {
		return false;
	}
}

function iwwAboButtonReadCache(key) {
	try {
		const cached = iwwAboButtonProductCache.get(key)
			|| JSON.parse(window.localStorage.getItem(key) || 'null');
		if (cached && Number.isFinite(cached.expiresAt)
			&& cached.expiresAt > Date.now() && Array.isArray(cached.products)
			&& cached.products.length > 0) {
			iwwAboButtonProductCache.set(key, cached);
			return cached.products;
		}
		// Eine alte leere Antwort darf den Button nicht eine Stunde lang blockieren.
		iwwAboButtonProductCache.delete(key);
		window.localStorage.removeItem(key);
	} catch {
		// Gesperrter Browser-Speicher verhindert den Abruf nicht.
	}
	return null;
}

function iwwAboButtonWriteCache(key, products) {
	if (products.length === 0) return;
	const cached = {expiresAt: Date.now() + iwwAboButtonCacheLifetime, products};
	iwwAboButtonProductCache.set(key, cached);
	try {
		window.localStorage.setItem(key, JSON.stringify(cached));
	} catch {
		// Ohne localStorage gilt der Cache fuer die aktuelle Seite.
	}
}

async function iwwAboButtonLoadProducts(url) {
	const key = iwwAboButtonCachePrefix + new URL(url, window.location.href).href;
	const cached = iwwAboButtonReadCache(key);
	if (cached !== null) return cached;
	if (iwwAboButtonProductRequests.has(key)) return iwwAboButtonProductRequests.get(key);
	const request = (async () => {
		const response = await fetch(url, {headers: {Accept: 'application/json'}, cache: 'no-store'});
		if (!response.ok) throw new Error('HTTP ' + response.status);
		const data = await response.json();
		if (!data || !Array.isArray(data.payload)) throw new Error('Ungueltige Produktantwort');
		iwwAboButtonWriteCache(key, data.payload);
		return data.payload;
	})();
	iwwAboButtonProductRequests.set(key, request);
	try {
		return await request;
	} finally {
		iwwAboButtonProductRequests.delete(key);
	}
}

function iwwAboButtonFindShopUrl(products, informationsdienst) {
	const normalize = value => String(value ?? '').toLowerCase();
	const product = products.find(candidate => candidate && typeof candidate === 'object'
		&& !Array.isArray(candidate)
		&& normalize(candidate.informationsdienst) === normalize(informationsdienst));
	const url = typeof product?.url === 'string' ? product.url.trim() : '';
	return iwwAboButtonIsWebUrl(url) ? url : '';
}

function iwwAboButtonHideWrapper(element) {
	// Der Wrapper traegt nur diesen Button; ohne Ziel bliebe sonst eine Luecke.
	// .hidden steht im Stylesheet hinter .flex und setzt sich deshalb durch.
	(element.closest('[data-abo-wrapper]') || element).classList.add('hidden');
}

function iwwAboButtonActivate(element, shopUrl) {
	element.dataset.aboUrl = shopUrl;
	element.href = shopUrl;
	element.target = '_blank';
	element.rel = 'noopener noreferrer';
	element.classList.remove('hidden');
	element.hidden = false;
}

async function iwwAboButtonLoad(element) {
	if (iwwAboButtonInitialized.has(element)) return;
	// Auch bereits gespeicherte Seiten ohne hidden-Klasse duerfen keinen Link ohne Ziel zeigen.
	element.classList.add('hidden');
	// Ein stehengebliebener Wrapper heisst: die Abo-Pruefung lief nicht. Dann
	// darf der Button nicht doch noch ein Ziel bekommen.
	if (element.closest('iww-dynamiccontent[type="uservisibility"]')) return;
	iwwAboButtonInitialized.add(element);
	const informationsdienst = (element.getAttribute('data-abo-infodienst') || '').trim();
	if (informationsdienst === '') {
		iwwAboButtonHideWrapper(element);
		return;
	}
	try {
		const parameters = new URLSearchParams({
			informationsdienste: informationsdienst,
			limit: iwwAboButtonProductLimit
		});
		const deliveryUrl = iwwAboButtonBaseUrl.replace(/\/$/, '') + '/api/v1/product/delivery?' + parameters;
		if (!iwwAboButtonIsWebUrl(deliveryUrl)) throw new Error('Ungueltige Product-Delivery-URL');
		const products = await iwwAboButtonLoadProducts(deliveryUrl);
		if (!element.isConnected) return;
		const shopUrl = iwwAboButtonFindShopUrl(products, informationsdienst);
		if (shopUrl === '') {
			// Lieber kein Button als einer, der ins Leere fuehrt.
			iwwAboButtonHideWrapper(element);
			return;
		}
		iwwAboButtonActivate(element, shopUrl);
	} catch {
		// Ein Ausfall des ProductRecommenders darf den Kopfbereich nicht beschaedigen.
		iwwAboButtonHideWrapper(element);
	}
}

function iwwAboButtonInit(root) {
	const elements = Array.from(root.querySelectorAll(iwwAboButtonSelector));
	if (root.matches && root.matches(iwwAboButtonSelector)) elements.unshift(root);
	return Promise.all(elements.map(iwwAboButtonLoad));
}

function iwwAboButtonStart() {
	iwwAboButtonInit(document);
	if (typeof MutationObserver === 'undefined' || !document.body) return;
	new MutationObserver(mutations => {
		mutations.forEach(mutation => mutation.addedNodes.forEach(node => {
			if (node instanceof Element) iwwAboButtonInit(node);
		}));
	}).observe(document.body, {childList: true, subtree: true});
}
document.addEventListener('htmx:afterSettle', event => iwwAboButtonInit(event.target));
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', iwwAboButtonStart);
} else {
	iwwAboButtonStart();
}



